import { useCallback } from "react";
import * as DocumentPicker from "expo-document-picker";

import { logger } from "@/utils/logger";

type SelectedFileType = {
  uri: string;
  fileName: string;
  mimeType: string;
  size?: number;
};

type FilePickerResultType = {
  isSuccess: boolean;
  files: SelectedFileType[];
};

const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

/**
 * Hook that provides file picking functionality for document uploads.
 * Supports picking documents and images via the system file picker.
 */
export const useFilePicker = () => {
  const pickFiles = useCallback(async (): Promise<FilePickerResultType> => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ALLOWED_DOCUMENT_TYPES,
        multiple: true,
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets?.length) {
        return { isSuccess: false, files: [] };
      }

      const files: SelectedFileType[] = result.assets.map(asset => ({
        uri: asset.uri,
        fileName: asset.name,
        mimeType: asset.mimeType ?? "application/octet-stream",
        size: asset.size,
      }));

      return { isSuccess: true, files };
    } catch (error) {
      logger.error({ error, message: "Error picking documents" });
      return { isSuccess: false, files: [] };
    }
  }, []);

  return {
    pickFiles,
  };
};

export type { SelectedFileType, FilePickerResultType };
